import { HeadMetadata } from '@/components/HeadMetadata';
import { ibm } from '@/utils/fonts/fonts';
import { datetimeInLocaleLanguageAndTimezone, timeAgo } from '@/utils/timeFormatters';
import { TTrackData } from '@/utils/types/TTrackData';
import { GetStaticProps, Metadata, NextPage } from 'next';
import Image from 'next/image';
import React, { ReactElement } from 'react';
import useSWR from 'swr';
import RiFileDamageFill from '~icons/ri/file-damage-fill'
import RiLoader5Fill from '~icons/ri/loader-5-fill'
import RiArrowRightUpLine from '~icons/ri/arrow-right-up-line'
import Link from 'next/link';
import RootLayout from './RootLayout';
import { NextPageWithLayout } from './_app';

interface Props {
  siteUrl: string;
}

const StreamedMusic: NextPageWithLayout<Props> = (props) => {
  const {data, error} = useSWR<{ items: TTrackData[] }>(
    'https://api.tijoe.net/track-history',
    async (input: RequestInfo, init: RequestInit) => {
      const res = await fetch(input, init);
      return res.json();
    }, { refreshInterval: 1000 * 10 }
  );

  if (error) return (
    <>
      <article className='max-w-4xl mx-auto flex justify-center'>
        <p className='flex flex-row gap-3 items-center bg-[#151515]/50 rounded-2xl w-fit px-5 p-3 text-sm border border-white/10'>
          Ошибка загрузки данных о последних треках
        </p>
      </article>
    </>
  )

  if (!data) return (
    <>
      <article className='max-w-4xl mx-auto flex justify-center'>
        <p className='flex flex-row gap-3 items-center bg-[#151515]/50 rounded-2xl w-fit px-5 p-3 text-sm border border-white/10'>
          Получение списка треков
          <RiLoader5Fill className='animate-spin'/>
        </p>
      </article>
    </>
  )

  return (
    <>
      <HeadMetadata
        pageTitle="Музыка со стримов › Тиджой"
        pageDescription="Музыка со стримов Тиджоя."
        canonicalUrl={`${props.siteUrl}/streamed-music`}
        robots="index,follow"
      />
      <section>
        <ul className='rounded-3xl bg-[#151515]/50 divide-y divide-white/5 w-full mx-auto overflow-hidden cursor-pointer'>
          {(data.items.map((track, index) =>
            <li
              key={index}
              onClick={() => window.open(track.url.lastfm, '_blank')}
              className='flex flex-row items-center justify-between md:hover:bg-[#282828]/50 transition-colors duration-300 ease-out'>
              <div className='md:text-base text-sm flex flex-row gap-3 items-center'>
                {track.images === null
                  ? <div className='md:w-16 md:h-16 w-14 h-14 items-center justify-center flex'><RiFileDamageFill/></div>
                  : <Image
                      src={track.images.medium.url}
                      alt={`Обложка трека ${track.name} от исполнителя ${track.artist}`}
                      width={track.images.medium.dimensions.width}
                      height={track.images.medium.dimensions.height}
                      className='w-14 h-14 md:w-16 md:h-16'
                  />
                }
                <div className='flex flex-col gap-0.5 leading-tight py-3 w-40 md:w-auto'>
                  <p className='line-clamp-2'>{track.name}</p>
                  <p className='md:text-sm text-xs text-zinc-400 line-clamp-1'>{track.artist}</p>
                </div>
              </div>
              <time 
                dateTime={track.date.iso}
                style={ibm.style}
                className='md:text-sm text-xs text-zinc-400 mx-3 md:mx-5'
                title={datetimeInLocaleLanguageAndTimezone(new Date(track.date.iso))}>
                  {timeAgo(new Date(track.date.iso), new Date())}
              </time>
            </li>
          ))}
        </ul>
      </section>
      <Link 
        href='https://www.last.fm/ru/user/TijoeMusic'
        target="_blank"
        className='text-sm md:text-base rounded-3xl bg-[#151515]/50 p-3 px-5 mt-5 w-fit flex flex-row items-center gap-5 mx-auto md:hover:bg-[#282828]/50 transition-colors duration-300 ease-out'>
        <p>Больше треков на LastFm </p><RiArrowRightUpLine className='text-lg'/>
      </Link>
    </>
  )
}

StreamedMusic.getLayout = function getLayout(page: ReactElement) {
  return (
      <RootLayout>{page}</RootLayout>
  )
}  

export default StreamedMusic;

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      siteUrl: process.env['SITE_URL'] as string,
    },
  };
};